import api from "@apiClient";
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import Table from "@components/admin/Table";
import PageNavigator from "@components/admin/PageNavigator";
import SearchBar from "@components/admin/SearchBar";
import apiClient from "@apiClient";
import { useNavigate } from "react-router-dom";
import { GROUP_ADD_MEMBERS } from "@constants/api";
import useSetActiveLink from "@hooks/useSetActiveLink";
import useTable from "@hooks/useTable";
import MyAvatar from "@components/admin/MyAvatar";
import useAdminLoggedIn from "@hooks/useAdminLoggedIn";
import useDateFilters from "@hooks/useDateFilters";
import { ArrowBack, MoreHoriz } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PopperWrapper from "@components/admin/PopperWrapper";
import PopperList from "@components/admin/PopperList";
import ConfirmationModal from "@components/admin/ConfirmationModal";
import UserDetails from "@components/admin/UserDetails";

function GroupDetails({ groupId, setId, community }) {
  useAdminLoggedIn();
  useSetActiveLink(3);
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);

  useEffect(() => {
    if (groupId) {
      apiClient
        .getGroup(groupId)
        .then(setGroup)
        .catch(() => {});
    }
  }, [groupId]);

  const {
    setReload,
    setPage,
    page,
    totalPages,
    count,
    query,
    setQuery,
    data,
    checked,
    setChecked,
    setLimit,
    limit,
    setSortColumns,
    sortColumns,
    loading,
    setDateFilters,
  } = useTable({
    getData: (defaultParams) =>
      apiClient.getGroupMembers(groupId, defaultParams),
    pageSize: 10,
  });

  const [userToRemove, setUserToRemove] = useState(null);
  const [showRemoveFromGroupModal, setShowRemoveFromGroupModal] =
    useState(false);
  const [showRemoveFromCommunityModal, setShowRemoveFromCommunityModal] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [memberMap, setMemberMap] = useState({});

  const handleRemoveFromGroupClick = (user) => {
    setUserToRemove(user);
    setShowRemoveFromGroupModal(true);
  };

  const handleRemoveFromGroup = () => {
    if (!userToRemove) return toast.error("No member selected");

    apiClient
      .removeMemberFromGroup({ memberId: userToRemove.id, groupId })
      .then((response) => {
        toast.success(response.data?.message ?? "Member removed successfully");
        setUserToRemove(null);
        setShowRemoveFromGroupModal(false);
        setReload((r) => r + 1);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleRemoveFromCommunity = () => {
    if (!userToRemove) return toast.error("No member selected");

    const communityId =
      community?.id || group?.community?.id || group?.community;
    console.log("communityId: ", group.community);

    apiClient
      .removeMemberFromCommunity({
        userId: userToRemove.id,
        communityId,
      })
      .then((response) => {
        toast.success(response.data?.message ?? "Member removed successfully");
        // refresh members table
        setReload((r) => r + 1);
        // refresh group details (to update counts/metadata)
        apiClient
          .getGroup(groupId)
          .then(setGroup)
          .catch(() => {});
        // mark memberMap for this user as not a member
        const uid =
          userToRemove?.id || userToRemove?._id || userToRemove?.userId;
        if (uid) setMemberMap((m) => ({ ...m, [uid]: false }));
        setUserToRemove(null);
        setShowRemoveFromCommunityModal(false);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCloseDialog = () => {
    setUserToRemove(null);
    setShowRemoveFromGroupModal(false);
    setShowRemoveFromCommunityModal(false);
  };

  const handleRemoveFromCommunityClick = (user) => {
    setUserToRemove(user);
    setShowRemoveFromCommunityModal(true);
  };

  const communityIdRaw =
    community?.id || group?.community?.id || group?.community;

  useEffect(() => {
    if (!communityIdRaw) return;
    if (!data || !data.length) return;

    data.forEach((item) => {
      const uid = item?.id || item?.userId || item?._id;
      if (!uid) return;
      if (typeof memberMap[uid] !== "undefined") return; // already fetched

      apiClient
        .checkCommunityMember({ userId: uid, communityId: communityIdRaw })
        .then((res) => {
          setMemberMap((m) => ({ ...m, [uid]: Boolean(res?.isMember) }));
        })
        .catch(() => {
          setMemberMap((m) => ({ ...m, [uid]: false }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, communityIdRaw]);

  // PROPS FOR TABLE
  const getMediaUrl = (media) => {
    if (!media) return null;
    return typeof media === "object" ? media.currBucketUrl : null;
  };

  const formatDate = (timestamp) =>
    timestamp ? new Date(Number(timestamp)).toLocaleString() : "-";

  const fields = [
    {
      columnName: "Name",
      field: "firstName",
      render: (item) => (
        <MyAvatar
          src={getMediaUrl(item?.media)}
          name={
            `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || "—"
          }
        />
      ),
    },

    {
      columnName: "Username",
      field: "userName",
      render: (item) => <span>{item?.userName || "—"}</span>,
    },

    {
      columnName: "Email",
      field: "emailAddress",
      render: (item) => <span>{item?.emailAddress || "—"}</span>,
    },

    {
      columnName: "Created On",
      field: "createdAt",
      render: (item) => <span>{formatDate(item?.createdAt)}</span>,
    },
    {
      columnName: "Actions",
      render: (item) => {
        // Hide everything if user is admin
        if (item.role?.includes("admin")) {
          return null;
        }

        // Otherwise show actions
        return (
          <PopperWrapper id={item.id}>
            <IconButton>
              <MoreHoriz />
            </IconButton>
            <PopperList
              items={(() => {
                const communityIdRaw =
                  community?.id || group?.community?.id || group?.community;
                const isMemberOfCommunity = (() => {
                  const communityId =
                    community?.id || group?.community?.id || group?.community;
                  console.log("community id", communityId);

                  if (!communityId) return true; // no community context -> allow

                  const uid = item?.id || item?.userId || item?._id;
                  console.log("uid: ", uid);

                  // If we have an authoritative result from the server, use it.
                  if (uid && typeof memberMap[uid] !== "undefined") {
                    return Boolean(memberMap[uid]);
                  }

                  // If server result not available yet, conservatively assume not a member
                  // to avoid enabling the action incorrectly. The memberMap effect
                  // will populate values shortly after data loads.
                  return false;
                })();

                return [
                  {
                    label: "View Profile",
                    onClick: () => setSelectedUser(item),
                  },
                  {
                    label: "Remove from Group",
                    onClick: () => handleRemoveFromGroupClick(item),
                  },
                  {
                    label: "Remove from Community",
                    onClick: () => handleRemoveFromCommunityClick(item),
                    disabled: !isMemberOfCommunity, // ❌ Disabled if NOT member
                  },
                ];
              })()}
            />
          </PopperWrapper>
        );
      },
    },
  ];

  const [eventsCount, setEventsCount] = useState(0);

  const fetchEventsCount = React.useCallback(() => {
    apiClient
      .getGroupEventsCount(groupId) // ✅ apiClient, not api — groupId, not id
      .then((res) => {
        setEventsCount(res.eventsCount || 0);
      })
      .catch(() => {});
  }, [groupId]);

  React.useEffect(() => {
    fetchEventsCount();
  }, [fetchEventsCount]);

  return (
    <div className="grow py-4 px-12">
      <div className="grid grid-cols-4 items-center mb-4">
        {/* LEFT */}
        <div className="flex gap-3">
          <IconButton className="self-center" onClick={() => setId(null)}>
            <ArrowBack />
          </IconButton>

          <MyAvatar
            name={group?.name}
            subtext={`${group?.members?.length || 0} users`}
            src={group?.icon?.currBucketUrl}
            subtextProps={{ style: { marginLeft: 5 } }}
            textProps={{
              className: "font-medium text-lg",
              style: { marginLeft: 5 },
            }}
            sx={{ height: 60, width: 60 }}
          />
        </div>
        {/* CENTER */}{" "}
        <div className="mx-12">
          <div className="px-5 py-1 flex justify-end">
            <Typography className="font-semibold text-gray-400 text-sm">
              Total resurfaced posts: {eventsCount || 0}
            </Typography>
          </div>
        </div>
        <div className="flex justify-center">
          <SearchBar setQuery={setQuery} query={query} />
        </div>
        {/* RIGHT — MEMBERS TAB STYLE */}
        <div className="flex justify-end items-center pr-4">
          <Button
            variant="text"
            sx={{
              color: "#10b981", // green text
              fontWeight: 500, // medium
              textTransform: "none", // remove uppercase
              borderBottom: "2px solid #10b981", // underline
              borderRadius: 0, // remove rounded
              paddingBottom: 0.2, // slight padding
              "&:hover": {
                background: "transparent",
                color: "#0e9e6e",
              },
            }}
          >
            MEMBERS
          </Button>
        </div>
      </div>

      <Table
        data={data}
        fields={fields}
        checked={checked}
        setChecked={setChecked}
        setSortColumns={setSortColumns}
        sortColumns={sortColumns}
      />

      {data.length > 0 && (
        <PageNavigator
          totalPages={totalPages}
          setPage={setPage}
          page={page}
          setLimit={setLimit}
          limit={limit}
        />
      )}

      <ConfirmationModal
        open={showRemoveFromGroupModal}
        setOpen={handleCloseDialog}
        buttons={[
          {
            variant: "contained",
            onClick: handleRemoveFromGroup,
            children: "Delete",
            color: "error",
          },
        ]}
      >
        Are you sure you want to remove this member from Group?
      </ConfirmationModal>

      <UserDetails
        user={selectedUser}
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        // actions={[]}
        onlyProfile={true}
      />

      <ConfirmationModal
        open={showRemoveFromCommunityModal}
        setOpen={handleCloseDialog}
        buttons={[
          {
            variant: "contained",
            onClick: handleRemoveFromCommunity,
            children: "Delete",
            color: "error",
          },
        ]}
      >
        Are you sure you want to remove this member from community?
      </ConfirmationModal>
    </div>
  );
}

export default GroupDetails;
